/**
 * Luma's reasoning trail — the accumulation rules behind the streamed thoughts.
 *
 * The agent emits a progress event per beat of work: a broad stroke (`stage`)
 * and sometimes a specific thought (`message`). We used to collapse the whole
 * stream into one status line that overwrote itself. Now each event that says
 * something new becomes its own immutable bubble, so the transcript reads as a
 * record of what Luma did rather than a single mutating label.
 *
 * Pure functions only — no network, no store. The trail lives for one turn and
 * is never persisted; see `deriveThought` for what counts as "something new".
 */

import type { LumaThought } from '@/types/api-response/chat-session-types';

/* ---------------------------------------------------------------------------------------------- */

/**
 * Human labels for the agent's stages. `acting` and `observing` share a label
 * because both are research beats; they still produce separate bubbles when
 * they carry different details.
 */
export const THOUGHT_STAGE_LABELS: Record<string, string> = {
   received: 'received',
   planning: 'planning',
   acting: 'researching',
   observing: 'researching',
   generating: 'summarizing',
   complete: 'complete',
};

/**
 * Stages that never earn a bubble of their own. `complete` fires immediately
 * before the answer, which supersedes it.
 */
const SILENT_STAGES = new Set(['complete']);

/** Label a raw stage; unknown stages pass through so new ones still show up. */
export function stageLabel(stage: string): string {
   return THOUGHT_STAGE_LABELS[stage] || stage;
}

/**
 * Strip the agent's own trailing ellipsis.
 *
 * Most progress messages arrive as "Searching for facilities..." — punctuation
 * that made sense for one mutating status line. As a settled bubble it reads as
 * still-running, and on the live row it collides with the ellipsis the view
 * appends ("Generating report......"). Normalising here means both render paths
 * get clean text, and two messages differing only in trailing dots dedupe.
 */
export function normalizeDetail(detail: string): string {
   return detail.replace(/[.…\s]+$/, '');
}

/* ---------------------------------------------------------------------------------------------- */

/**
 * Work out what a progress event adds to the trail.
 *
 * Returns the thought to append, or `null` when the event says nothing new:
 *
 * - a silent stage (`complete`)
 * - an exact repeat of the latest thought
 * - a bare stage change that repeats the latest thought's stage — once
 *   "researching · reading 42 documents" is on screen, a detail-less
 *   `observing` beat adds no information
 *
 * Callers append the result rather than mutating in place, so every bubble that
 * reaches the screen stays exactly as it was rendered.
 */
export function deriveThought(
   trail: readonly LumaThought[],
   stage: string,
   detail: string,
): LumaThought | null {
   if (SILENT_STAGES.has(stage))
      return null;

   const label = stageLabel(stage);
   if (!label)
      return null;

   const trimmed = normalizeDetail(detail.trim());
   const latest = trail.at(-1);

   if (latest && latest.stage === label) {
      // Same beat restated, or a beat that adds no detail to one already shown.
      if (!trimmed || latest.detail === trimmed)
         return null;
   }

   return { stage: label, detail: trimmed };
}

/* ---------------------------------------------------------------------------------------------- */

/** What a bubble reads as: the specific thought when there is one, else the beat. */
export function thoughtText(thought: LumaThought): string {
   return thought.detail || thought.stage;
}
